import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { AudioTurnEvent } from '@common/audio-note/audio-note.events';
import type { VoiceTurnRecord } from '../api/VoiceTurns';
import {
  consumeVoiceTurnStream,
  createVoiceTurnStream,
  getVoiceTurn,
  getVoiceTurnAudioBlob,
} from '../api/VoiceTurns';
import { AudioNoteRecorder, canRecordAudio, type RecordedAudioNote } from '../audio/recorder';
import { ReplayPlayer } from '../audio/replay-player';
import { StreamPlaybackController, type LivePlaybackSnapshot } from '../audio/stream-playback';
import { useUserInfoContext } from '../contexts/UserInfoContext';

export interface SendVoiceTurnParams {
  personaId: string;
  chatId?: string;
  companionId?: string;
}

export interface UseVoiceTurnsState {
  status: 'idle' | 'recording' | 'ready' | 'sending' | 'completed' | 'error';
  selectedFile: File | null;
  selectedFileDurationMs?: number;
  chatId?: string;
  turnId?: string;
  transcript: string;
  streamedAssistantText: string;
  finalAssistantText: string;
  replayAudioUrl?: string;
  error?: string;
  lastTurn?: VoiceTurnRecord;
  livePlayback: LivePlaybackSnapshot;
  events: AudioTurnEvent[];
  recordingSupported: boolean;
}

export function useVoiceTurns() {
  const { userInfo } = useUserInfoContext();
  const recorder = useMemo(() => new AudioNoteRecorder(), []);
  const replayPlayer = useMemo(() => new ReplayPlayer(), []);
  const livePlayback = useMemo(() => new StreamPlaybackController(), []);
  const objectUrlRef = useRef<string | undefined>();
  const abortControllerRef = useRef<AbortController | null>(null);

  const [state, setState] = useState<UseVoiceTurnsState>({
    status: 'idle',
    selectedFile: null,
    transcript: '',
    streamedAssistantText: '',
    finalAssistantText: '',
    livePlayback: livePlayback.getSnapshot(),
    events: [],
    recordingSupported: canRecordAudio(),
  });

  useEffect(() => {
    return () => {
      replayPlayer.dispose();
      revokeObjectUrl(objectUrlRef.current);
      abortControllerRef.current?.abort();
      recorder.cancel();
    };
  }, [recorder, replayPlayer]);

  const setSelectedRecording = useCallback((recordedNote: RecordedAudioNote) => {
    revokeObjectUrl(objectUrlRef.current);
    setState((current) => ({
      ...current,
      status: 'ready',
      selectedFile: recordedNote.file,
      selectedFileDurationMs: recordedNote.durationMs,
      error: undefined,
    }));
  }, []);

  const selectFile = useCallback((file: File | null) => {
    revokeObjectUrl(objectUrlRef.current);
    setState((current) => ({
      ...current,
      status: file ? 'ready' : 'idle',
      selectedFile: file,
      selectedFileDurationMs: undefined,
      error: undefined,
      transcript: '',
      streamedAssistantText: '',
      finalAssistantText: '',
      replayAudioUrl: undefined,
      livePlayback: livePlayback.reset(),
      events: [],
    }));
  }, [livePlayback]);

  const startRecording = useCallback(async () => {
    await recorder.start();
    setState((current) => ({
      ...current,
      status: 'recording',
      error: undefined,
    }));
  }, [recorder]);

  const stopRecording = useCallback(async () => {
    const recordedNote = await recorder.stop();
    setSelectedRecording(recordedNote);
  }, [recorder, setSelectedRecording]);

  const cancelActiveTurn = useCallback(() => {
    abortControllerRef.current?.abort();
    abortControllerRef.current = null;
  }, []);

  const sendSelectedNote = useCallback(async (params: SendVoiceTurnParams) => {
    if (!userInfo?.accessToken) {
      throw new Error('You must be signed in to send a voice note.');
    }

    if (!state.selectedFile) {
      throw new Error('Select or record an audio note before sending.');
    }

    cancelActiveTurn();
    revokeObjectUrl(objectUrlRef.current);
    objectUrlRef.current = undefined;
    livePlayback.reset();

    const controller = new AbortController();
    abortControllerRef.current = controller;
    const idempotencyKey = crypto.randomUUID();
    let completedTurnId: string | undefined;

    setState((current) => ({
      ...current,
      status: 'sending',
      transcript: '',
      streamedAssistantText: '',
      finalAssistantText: '',
      replayAudioUrl: undefined,
      error: undefined,
      events: [],
      livePlayback: livePlayback.getSnapshot(),
    }));

    try {
      const response = await createVoiceTurnStream({
        accessToken: userInfo.accessToken,
        file: state.selectedFile,
        personaId: params.personaId,
        chatId: params.chatId,
        companionId: params.companionId,
        durationMs: state.selectedFileDurationMs,
        idempotencyKey,
        signal: controller.signal,
      });

      await consumeVoiceTurnStream(response, async (event) => {
        setState((current) => {
          const nextEvents = [...current.events, event];

          switch (event.type) {
            case 'turn.accepted':
              return {
                ...current,
                status: 'sending',
                chatId: event.chatId,
                turnId: event.turnId,
                events: nextEvents,
              };
            case 'transcript.final':
              return {
                ...current,
                status: 'sending',
                transcript: event.text,
                events: nextEvents,
              };
            case 'response.text.delta':
              return {
                ...current,
                status: 'sending',
                streamedAssistantText: `${current.streamedAssistantText}${event.text}`,
                events: nextEvents,
              };
            case 'response.audio.start':
              return {
                ...current,
                status: 'sending',
                livePlayback: livePlayback.beginStream({
                  format: event.format,
                  sampleRateHz: event.sampleRateHz,
                  channels: event.channels,
                }),
                events: nextEvents,
              };
            case 'response.audio.chunk':
              return {
                ...current,
                status: 'sending',
                livePlayback: livePlayback.appendChunk(event.dataBase64),
                events: nextEvents,
              };
            case 'response.audio.end':
              return {
                ...current,
                status: 'sending',
                livePlayback: livePlayback.endStream(),
                events: nextEvents,
              };
            case 'turn.complete':
              return {
                ...current,
                status: 'completed',
                turnId: event.turnId,
                chatId: event.chatId,
                finalAssistantText: event.assistantText,
                livePlayback: livePlayback.endStream(),
                events: nextEvents,
              };
            case 'error':
              return {
                ...current,
                status: 'error',
                error: event.message,
                events: nextEvents,
              };
            default:
              return {
                ...current,
                events: nextEvents,
              };
          }
        });

        if (event.type === 'turn.complete' && userInfo.accessToken) {
          completedTurnId = event.turnId;
          // TODO(audio-notes-ui): swap this eager blob download for a smarter
          // handoff if replay should start from signed URLs or cached assets.
          const blob = await getVoiceTurnAudioBlob(event.turnId, userInfo.accessToken);
          const objectUrl = URL.createObjectURL(blob);
          revokeObjectUrl(objectUrlRef.current);
          objectUrlRef.current = objectUrl;
          replayPlayer.load(objectUrl);

          setState((current) => ({
            ...current,
            replayAudioUrl: objectUrl,
          }));
        }
      }, controller.signal);

      if (completedTurnId && userInfo.accessToken) {
        const turn = await getVoiceTurn(completedTurnId, userInfo.accessToken);
        setState((current) => ({
          ...current,
          lastTurn: turn,
        }));
      }
    } catch (error) {
      setState((current) => ({
        ...current,
        status: 'error',
        error: error instanceof Error ? error.message : 'Voice note request failed.',
      }));
      throw error;
    } finally {
      abortControllerRef.current = null;
    }
  }, [
    cancelActiveTurn,
    livePlayback,
    replayPlayer,
    state.selectedFile,
    state.selectedFileDurationMs,
    userInfo,
  ]);

  const loadTurnById = useCallback(async (turnId: string) => {
    if (!userInfo?.accessToken) {
      throw new Error('You must be signed in to load a voice note turn.');
    }

    const turn = await getVoiceTurn(turnId, userInfo.accessToken);
    const nextState: Partial<UseVoiceTurnsState> = {
      turnId: turn.turnId,
      chatId: turn.chatId,
      transcript: turn.transcript || '',
      finalAssistantText: turn.assistantText || '',
      lastTurn: turn,
      status: turn.status === 'failed' ? 'error' : turn.status === 'completed' ? 'completed' : 'sending',
    };

    if (turn.status === 'completed') {
      // TODO(audio-notes-ui): when history screens wire into voice turns, move
      // replay-asset hydration behind a cache so revisiting a turn is cheaper.
      const blob = await getVoiceTurnAudioBlob(turn.turnId, userInfo.accessToken);
      const objectUrl = URL.createObjectURL(blob);
      revokeObjectUrl(objectUrlRef.current);
      objectUrlRef.current = objectUrl;
      replayPlayer.load(objectUrl);
      nextState.replayAudioUrl = objectUrl;
    }

    setState((current) => ({
      ...current,
      ...nextState,
    }));
  }, [replayPlayer, userInfo]);

  const reset = useCallback(() => {
    cancelActiveTurn();
    revokeObjectUrl(objectUrlRef.current);
    objectUrlRef.current = undefined;
    replayPlayer.dispose();
    recorder.cancel();

    setState({
      status: 'idle',
      selectedFile: null,
      transcript: '',
      streamedAssistantText: '',
      finalAssistantText: '',
      livePlayback: livePlayback.reset(),
      events: [],
      recordingSupported: canRecordAudio(),
    });
  }, [cancelActiveTurn, livePlayback, recorder, replayPlayer]);

  return {
    state,
    replayPlayer,
    selectFile,
    startRecording,
    stopRecording,
    sendSelectedNote,
    loadTurnById,
    cancelActiveTurn,
    reset,
  };
}

function revokeObjectUrl(value?: string) {
  if (value) {
    URL.revokeObjectURL(value);
  }
}
